import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Checkbox, DriftSnackbar } from 'drift-ui';
import cx from 'classnames';
import { DO_APPLICATION_TYPE } from 'client-experience-toolbox';
import ContinueButton from 'components/ContinueButton';
import PdfStateDocumentViewer from 'components/PdfStateDocumentViewer';
import { useApplication } from 'hooks/useApplication';
import { AgreementsMetadata } from 'services/digitalOnboardingService';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import baseStyles from './BaseFormStyles.scss';
import styles from './TermsAndPrivacy.scss';

export const TermsAndPrivacy = () => {
  const dispatch = useDispatch();
  const { application, section } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const [termsChecked, setTermsChecked] = useState(
    Boolean(application?.sectionsCompletion?.termsAndPrivacyPolicy?.completedAt),
  );
  const [privacyChecked, setPrivacyChecked] = useState(
    Boolean(application?.sectionsCompletion?.termsAndPrivacyPolicy?.completedAt),
  );
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [agreementData, setAgreementData] = useState<Uint8Array | null>(null);
  const [agreementMetadata, setAgreementMetadata] = useState<AgreementsMetadata | null>(null);
  const [agreementsLoading, setAgreementsLoading] = useState(true);
  const [agreementsError, setAgreementsError] = useState(false);
  const { setSectionComplete, getAgreements, updateAgreementMetadata } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.BANKING,
  );
  const { t } = useTranslation();

  const fetchAgreementPdf = useCallback(async () => {
    try {
      setAgreementsLoading(true);
      setAgreementsError(false);
      const { pdfData, metadata } = await getAgreements(DO_APPLICATION_TYPE.BANKING, 'cardholderAgreement');
      setAgreementMetadata(metadata);
      setAgreementData(pdfData);
      setNumPages(null);
    } catch {
      setAgreementsError(true);
    } finally {
      setAgreementsLoading(false);
    }
  }, [getAgreements]);

  const onContinue = async () => {
    if (!termsChecked || !privacyChecked) {
      setError(true);
      return;
    }
    try {
      setIsLoading(true);
      await updateAgreementMetadata('cardholderAgreement', agreementMetadata ?? null);
      const sectionsCompletion = {
        termsAndPrivacyPolicy: { completedAt: Date.now() },
      };
      await setSectionComplete(sectionsCompletion);
      dispatch({ type: 'SET_BANKING_SECTION', bankingSection: section + 1 });
    } catch {
      setSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgreementPdf();
  }, [fetchAgreementPdf]);

  return (
    <div className={cx(baseStyles.formContainer, styles.termsAndPrivacyFormContainer)}>
      <div className={styles.container}>
        <p className={cx(styles.descriptionText, agreementsLoading && styles.noRightPadding)}>
          {t('digital_onboarding.banking.terms_and_privacy.page_description')}
        </p>
        <div className={styles.pdfContainer}>
          <PdfStateDocumentViewer
            fileData={agreementData}
            isLoading={agreementsLoading}
            hasError={agreementsError}
            numPages={numPages}
            onRefresh={fetchAgreementPdf}
            onLoadSuccess={(loadedPages) => {
              setNumPages(loadedPages);
              setAgreementsLoading(false);
            }}
            onLoadError={(pdfLoadError) => {
              console.error('Error loading terms/privacy PDF:', pdfLoadError);
              setAgreementsLoading(false);
              setAgreementsError(true);
            }}
            pageKeyPrefix="terms_page"
          />
        </div>
        {!agreementsError && !agreementsLoading && (
          <>
            <div className={cx(styles.checkboxContainer, error && styles.errorCheckboxContainer)}>
              <p className={styles.checkboxTitle}>{t('digital_onboarding.banking.terms_and_privacy.checkbox_title')}</p>
              {error && (
                <p className={styles.errorText}>{t('digital_onboarding.banking.terms_and_privacy.error_text')}</p>
              )}

              <label className={styles.checkbox}>
                <Checkbox
                  checked={termsChecked}
                  slotProps={{
                    input: {
                      'data-nid-target': 'terms-privacy-terms-checkbox',
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  onChange={() => {
                    setError(false);
                    setTermsChecked((prev) => !prev);
                  }}
                />
                <p className={styles.checkboxText}>
                  {t('digital_onboarding.banking.terms_and_privacy.terms_checkbox')}
                </p>
              </label>
              <label className={styles.checkbox}>
                <Checkbox
                  checked={privacyChecked}
                  slotProps={{
                    input: {
                      'data-nid-target': 'terms-privacy-privacy-checkbox',
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  onChange={() => {
                    setError(false);
                    setPrivacyChecked((prev) => !prev);
                  }}
                />
                <p className={styles.checkboxText}>
                  {t('digital_onboarding.banking.terms_and_privacy.privacy_checkbox')}
                </p>
              </label>
            </div>
            <div className={styles.buttonContainer}>
              <ContinueButton
                title={t('digital_onboarding.common_section.agree_and_continue')}
                buttonStyles={styles.buttonStyles}
                handleContinueClick={onContinue}
                isPatching={isLoading}
              />
            </div>
          </>
        )}
      </div>
      <DriftSnackbar
        open={snackbar}
        variant="error"
        message={t('please_try_again')}
        autoHideDuration={3000}
        onClose={(prev) => setSnackbar(prev)}
      />
    </div>
  );
};
